'use client';

import { useEffect, useMemo, useState } from 'react';
import type {
  ContactEmailValidationEntry,
  ContactEmailValidationMap,
} from '@/lib/contact-email-validation';
import {
  collectUniqueEmailsForValidation,
  normalizeEmailForValidation,
} from '@/lib/contact-email-validation';

type EmailValidationApiResponse = {
  validations?: ContactEmailValidationEntry[];
};

/**
 * Seed every display email to a `pending` entry keyed by its normalized form.
 * An empty map means "no emails to validate"; a map with `pending` entries
 * means "validation in flight" (this is what the selector reads).
 */
export function seedPending(displayEmails: string[]): ContactEmailValidationMap {
  return displayEmails.reduce<ContactEmailValidationMap>((acc, email) => {
    const normalized = normalizeEmailForValidation(email);
    if (normalized) {
      acc[normalized] = { email, status: 'pending' };
    }
    return acc;
  }, {});
}

/**
 * Map the API validation entries into a normalized-keyed map, backfilling any
 * requested email that did not come back as `unknown`.
 */
export function buildValidationMap(
  entries: ContactEmailValidationEntry[],
  displayEmails: string[]
): ContactEmailValidationMap {
  const byEmail = entries.reduce<ContactEmailValidationMap>((acc, entry) => {
    const normalized = normalizeEmailForValidation(entry.email);
    if (!normalized) {
      return acc;
    }
    acc[normalized] = {
      email: entry.email,
      status: entry.status,
      detail: entry.detail,
      validatedAt: entry.validatedAt,
    };
    return acc;
  }, {});

  displayEmails.forEach((email) => {
    const normalized = normalizeEmailForValidation(email);
    if (!normalized || byEmail[normalized]) {
      return;
    }
    byEmail[normalized] = {
      email,
      status: 'unknown',
      detail: 'Validation did not return a result',
    };
  });

  return byEmail;
}

/**
 * Mark every display email `unknown` with the supplied detail (fetch failure).
 */
export function buildErrorMap(
  displayEmails: string[],
  detail: string
): ContactEmailValidationMap {
  return displayEmails.reduce<ContactEmailValidationMap>((acc, email) => {
    const normalized = normalizeEmailForValidation(email);
    if (!normalized) {
      return acc;
    }
    acc[normalized] = { email, status: 'unknown', detail };
    return acc;
  }, {});
}

/**
 * Fetch deliverability validation for a set of emails.
 *
 * Seeds all emails to `pending`, POSTs `/api/outreach/email-validations`, and
 * resolves to a normalized-keyed `ContactEmailValidationMap`. Aborts in-flight
 * requests on unmount / args change. This is the single source of truth for
 * recipient validation shared by the Outreach dialog and the Simple Search deck.
 */
export function useEmailValidations(
  emails: Array<string | null | undefined>,
  contactId?: string,
  enabled: boolean = true
): { validations: ContactEmailValidationMap; isValidating: boolean } {
  const [validations, setValidations] = useState<ContactEmailValidationMap>({});
  const [isValidating, setIsValidating] = useState(false);

  const displayEmails = useMemo(
    () => collectUniqueEmailsForValidation(emails),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [emails.join('|')]
  );
  const displayEmailsKey = useMemo(() => displayEmails.join('|'), [displayEmails]);

  useEffect(() => {
    if (!enabled || displayEmails.length === 0) {
      setValidations({});
      setIsValidating(false);
      return;
    }

    setValidations(seedPending(displayEmails));
    setIsValidating(true);

    const controller = new AbortController();
    let active = true;

    const validate = async () => {
      try {
        const response = await fetch('/api/outreach/email-validations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contactId, emails: displayEmails }),
          signal: controller.signal,
          cache: 'no-store',
        });

        if (!response.ok) {
          throw new Error('Failed to validate contact emails');
        }

        const payload = (await response.json()) as EmailValidationApiResponse;
        const next = buildValidationMap(payload.validations ?? [], displayEmails);
        if (active) {
          setValidations(next);
          setIsValidating(false);
        }
      } catch (error) {
        if (controller.signal.aborted || !active) {
          return;
        }
        setValidations(
          buildErrorMap(
            displayEmails,
            error instanceof Error ? error.message : 'Failed to validate email'
          )
        );
        setIsValidating(false);
      }
    };

    void validate();

    return () => {
      active = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayEmailsKey, contactId, enabled]);

  return { validations, isValidating };
}
